import { AddIcon } from '@chakra-ui/icons';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { SortingState, Updater } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionsTabsStatus } from '@blockpulse3/data/shared';
import {
  CreateSubscriptionInput,
  CreateSubscriptionMutation,
  FundraisingStatus,
  FundraisingType,
  OrderBy,
  OrderByDirection,
  SubscriptionFilterType,
  SubscriptionInfosFragment,
  SubscriptionWorkflowType,
  SubscriptionsFilterInput,
  UpdateSubscriptionMutation,
  useGetFundraisingQuery,
  useGetSubscriptionsCountQuery,
  useGetSubscriptionsQuery,
} from '@blockpulse3/graphql/hooks';
import { useSuccessToast } from '@blockpulse3/ui/commons';
import { usePagination } from '@blockpulse3/ui/ui-hooks';

import {
  FundraisingSubscribersCSVModal,
  FundraisingSubscribersModal,
} from '../../FundraisingSubscribersModal';
import { fundraisingSubscriberDefaultValues } from '../../NewFundraising/NewPrivateFundraising/NewPrivateFundraisingSubscribers/schema';
import { SubscriptionDetailsSidePanel } from '../SubscriptionDetailsSidePanel';
import { FundraisingSubscribersControls } from './FundraisingSubscribersControls';
import { FundraisingSubscribersRemindersModal } from './FundraisingSubscribersRemindersModal';
import { FundraisingSubscribersTable } from './FundraisingSubscribersTable';
import { getTabsWithCount } from './utils';

type Props = {
  /* ** Count of subscriptions by page ** */
  pageSize?: number;
};

/**
 * FundraisingSubscribersList.
 * Display title and filters of fundraising subscribers table.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FundraisingSubscribersList({ pageSize = 10 }: Props): JSX.Element {
  const i18nFundraisingSubscribersTable = useTranslations('FundraisingSubscribersTable');
  const i18nSubscriptionsTabsStatus = useTranslations(
    'FundraisingSubscribersTable.SubscriptionsTabsStatus',
  );

  const { fundraisingId = '' } = useParams();

  const [tabIndex, setTabIndex] = useState<number>(0);
  /* ** All filters ** */
  const [filters, setFilters] = useState<SubscriptionsFilterInput[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const reminderModal = useDisclosure();
  const csvModal = useDisclosure();
  const subscriberModal = useDisclosure();

  const successToast = useSuccessToast();

  const { data: fundraisingData } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
  });

  const subscriptionsCountsReq = useGetSubscriptionsCountQuery({
    variables: { fundraisingId },
    fetchPolicy: 'cache-and-network',
  });

  // For now only handle unique column sorting
  const orderBy: OrderBy | undefined = useMemo(() => {
    if (!sorting.length) return undefined;
    return {
      direction: sorting[0].desc ? OrderByDirection.DESC : OrderByDirection.ASC,
      field: sorting[0].id,
    };
  }, [sorting]);

  const paginationProps = usePagination({
    useQuery: useGetSubscriptionsQuery,
    variables: {
      fundraisingId,
      filterBy: filters,
      first: pageSize,
      orderBy,
    },
    dataName: 'subscriptions',
    pageSize,
  });

  const { totalCount, refetch } = paginationProps;

  /* ** TODO: remove effect, use event methods ** */
  useEffect(() => {
    refetch();
  }, [fundraisingId, filters, sorting, refetch]);

  const fundraising = fundraisingData?.fundraising;
  const isStarted = fundraising?.status === FundraisingStatus.STARTED;
  const isCrowdfunding = fundraising?.type === FundraisingType.CROWDFUNDING;
  const subscriptionsCounts = subscriptionsCountsReq.data?.getSubscriptionsCount;
  const mifidStep = fundraising?.subscriptionSteps?.find(
    (step) => step.__typename === 'MifidStepType',
  );
  const hasMifid = !!mifidStep;
  const isAllInSubscriptionWorkflow =
    fundraising?.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;
  const tabs = getTabsWithCount(subscriptionsCounts, hasMifid, isAllInSubscriptionWorkflow);

  const handleSubscriberModalComplete = (
    data: CreateSubscriptionMutation | UpdateSubscriptionMutation,
    subscription: CreateSubscriptionInput,
    isLegal: boolean,
  ): void => {
    successToast({ title: i18nFundraisingSubscribersTable('Toast.InvitationSuccess') });
    refetch();
    subscriptionsCountsReq.refetch();
  };

  /* ** Refetch subscriptions() on filter modal submit ** */
  const handleFiltersSubmit = (values: SubscriptionsFilterInput[]): void => {
    const searchValueFilter = filters.filter((filter) =>
      ['searchValue', 'investStatus'].includes(filter.name),
    );
    setFilters([...values, ...searchValueFilter]);
  };

  const handleSearchInputSubmit = (value: string): void => {
    const isInputAndFilterSearchEmpty =
      value === '' && !filters.some((filter) => filter.name === 'searchValue');

    if (isInputAndFilterSearchEmpty) return;

    resetSpecificFilter('searchValue', SubscriptionFilterType.CUSTOM, value);
  };

  const resetSpecificFilter = (
    filterName: string,
    filterType: SubscriptionFilterType,
    value: string,
  ): void => {
    const currentFilterWithoutFilterName = filters.filter((filter) => filter.name !== filterName);
    const newFilter: SubscriptionsFilterInput = {
      name: filterName,
      type: filterType,
      value,
    };
    setFilters([...currentFilterWithoutFilterName, newFilter]);
  };

  const handleTabsChange = (index: number): void => {
    setTabIndex(index);

    const newFilters: SubscriptionsFilterInput[] = [];
    if (tabs[index].name !== SubscriptionsTabsStatus.ALL_STATUS) {
      newFilters.push({
        name: 'investStatus',
        type: SubscriptionFilterType.STATUS,
        value: tabs[index].name,
      });
    }

    if (
      [SubscriptionsTabsStatus.PENDING, SubscriptionsTabsStatus.ALL_STATUS].includes(
        tabs[index].name,
      )
    ) {
      const resetFilterStatus = filters.filter((filter) => filter.name !== 'investStatus');
      setFilters([...newFilters, ...resetFilterStatus]);
    } else {
      const resetFilterStatus = filters.filter((filter) => filter.name === 'searchValue');
      setFilters([...newFilters, ...resetFilterStatus]);
    }
  };

  const handleSortingChange = (sortFn: Updater<SortingState>): void => {
    setSorting(sortFn);
  };

  const handleRowClick = (id: SubscriptionInfosFragment['id']): void => {
    setSubscriptionId(id);
    onOpen();
  };

  return (
    <>
      {subscriptionId && (
        <SubscriptionDetailsSidePanel
          blockScrollOnMount={false}
          isOpen={isOpen}
          subscriptionId={subscriptionId}
          onClose={onClose}
          onDeleteRow={refetch}
        />
      )}
      <Card>
        <CardHeader
          alignItems={{ base: 'flex-start', md: 'center' }}
          as={Stack}
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing="4"
        >
          <Heading size="md">{i18nFundraisingSubscribersTable('SubscribersList')}</Heading>
          <Stack alignSelf="stretch" direction={{ base: 'column', md: 'row' }}>
            {isCrowdfunding && (
              <>
                <Button
                  isDisabled={!isStarted}
                  leftIcon={<AddIcon />}
                  onClick={subscriberModal.onOpen}
                >
                  {i18nFundraisingSubscribersTable('InviteSubscriber')}
                </Button>
                <Button isDisabled={!isStarted} variant="secondary" onClick={csvModal.onOpen}>
                  {i18nFundraisingSubscribersTable('ImportCSV')}
                </Button>
              </>
            )}
            {isStarted && (
              <Button isDisabled={!totalCount} variant="secondary" onClick={reminderModal.onOpen}>
                {i18nFundraisingSubscribersTable('SendReminder', {
                  nb: totalCount,
                })}
              </Button>
            )}
          </Stack>
        </CardHeader>
        <CardBody>
          <Tabs defaultIndex={0} onChange={handleTabsChange}>
            <TabList>
              {tabs.map((tab, i) => (
                <Tab key={i}>
                  <Text>
                    {tab.name === SubscriptionsTabsStatus.NOT_SIGNED_UP &&
                    [FundraisingStatus.DRAFT, FundraisingStatus.VALIDATED].includes(
                      fundraising?.status || FundraisingStatus.DRAFT,
                    )
                      ? i18nSubscriptionsTabsStatus(`${tab.name}_PENDING`)
                      : i18nSubscriptionsTabsStatus(tab.name)}
                  </Text>
                  <Badge ml="2">{tab.count}</Badge>
                </Tab>
              ))}
            </TabList>
            <TabPanels pt="4">
              <Stack spacing="4">
                <FundraisingSubscribersControls
                  activeTabIndex={tabIndex}
                  filters={filters}
                  tabs={tabs}
                  onFiltersChange={handleFiltersSubmit}
                  onSearchInputSubmit={handleSearchInputSubmit}
                />
                <FundraisingSubscribersTable
                  fundraisingStatus={fundraising?.status}
                  sorting={sorting}
                  tabName={tabs[tabIndex].name}
                  onRowClick={handleRowClick}
                  onSortingChange={handleSortingChange}
                  {...paginationProps}
                />
              </Stack>
            </TabPanels>
          </Tabs>
        </CardBody>
        <FundraisingSubscribersRemindersModal
          filters={filters}
          fundraisingId={fundraisingId}
          isOpen={reminderModal.isOpen}
          totalCount={totalCount}
          onClose={reminderModal.onClose}
        />
        {isCrowdfunding && (
          <>
            <FundraisingSubscribersCSVModal
              fundraisingId={fundraisingId}
              isCrowdfunding={isCrowdfunding}
              isOpen={csvModal.isOpen}
              onClose={csvModal.onClose}
              onComplete={refetch}
            />
            <FundraisingSubscribersModal
              defaultValues={fundraisingSubscriberDefaultValues}
              fundraisingId={fundraisingId}
              isCrowdfunding={isCrowdfunding}
              isEditing={false}
              isOpen={subscriberModal.isOpen}
              onClose={subscriberModal.onClose}
              onComplete={handleSubscriberModalComplete}
            />
          </>
        )}
      </Card>
    </>
  );
}
